import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Tooltip,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { userInformationAtom } from '../../Services/_states/auth.js';
import { ConstantActions } from '../../Services/_actions/index.js';
import { globalpermissions } from '../../Services/Permission.js';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ClearIcon from '@mui/icons-material/Clear';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LoadingButton from '../Shared/Loading/index.tsx';
import { AiActions } from '../../Services/_actions/index.js';
import { useSnackBar } from '../FlowBuilder/Common/SnackBar/index.tsx';
import OrganisationDropdown from '../Shared/OrganisationDropdown/index.tsx';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import EditIcon from '@mui/icons-material/Edit';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import ChatIcon from '@mui/icons-material/Chat';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useWebSocketAiContext } from '../../utils/WebSocketContext.tsx';
// import BeautifyText from '../AiChatbot/BeautifyText.tsx';
import BeautifyMarkdown from '../AiChatbot/Beautifymarkdown.tsx';

/* ────────────────────────────────────────────────────────────────────────────
   STRICT INSTRUCTION SET
   The model is told, on every single call, to emit ONLY a JSON object that
   matches this exact schema. This is what lets one free-text query like
   "create 11th computer science" or "create class 11 chemistry" turn into a
   complete, ready-to-save knowledge map with no follow-up questions.
──────────────────────────────────────────────────────────────────────────── */
const KNOWLEDGE_MAP_INSTRUCTION_SET = `# KNOWLEDGE MAP GENERATOR - STRICT JSON OUTPUT

## CRITICAL: YOU MUST RETURN ONLY VALID JSON
- **DO NOT** return any text, explanations, markdown, or code blocks
- **DO NOT** wrap in \`\`\`json or any other formatting
- **ONLY** return the JSON object
- The response must be parseable by JSON.parse() directly
- Never ask a clarifying question. If the user's request is underspecified
  (e.g. "create 11th computer science"), infer sensible defaults yourself:
  pick the most common board (e.g. CBSE/NCERT for Indian classes) and the
  standard textbook(s) and syllabus topics for that subject and class, and
  generate the full map in one shot.

## 1. OBJECTIVE
Generate a comprehensive knowledge map with topics, subtopics, prerequisites, and learning materials
for the subject/class implied by the user's query.

## 2. JSON STRUCTURE (EXACT - FOLLOW THIS EXACTLY)
{
  "subject": "Subject Name",
  "class": "Class/Grade",
  "board": "Board Name",
  "books": ["Book 1", "Book 2"],
  "topics": [
    {
      "topic": "Topic Name",
      "topic_prerequisites": ["Prerequisite 1", "Prerequisite 2","Prerequisite 3, "Prerequisite 4, "Prerequisite 5],
      "subtopics": [
        {
          "subtopic": "Subtopic Name",
          "prerequisites": [
            {
              "id": "pre_topic_subtopic_0_0",
              "name": "Prerequisite Name",
              "children": [
                {
                  "id": "pre_topic_subtopic_0_1",
                  "name": "Child Prerequisite",
                  "children": []
                }
              ]
            }
          ],
          "marks": 5
        }
      ],
      "learning_material": ["Material 1", "Material 2"],
      "description": "Topic description"
    }
  ]
}

## 3. RULES
- Cover the FULL syllabus for the subject/class: generate 4-6 topics (not just 2-3) unless the
  user explicitly names fewer topics.
- Each topic should have 3-6 subtopics.
- Prerequisites should have 3-5 levels of depth.
- Marks should be between 1-20 and roughly reflect real exam weightage.
- Use proper ID format: pre_topicname_subtopic_index_level_index
- Match complexity to the specified class level.

## 4. IMPORTANT
Return ONLY the JSON object. No text before, after, or around it.

## 5. EXAMPLE OUTPUT (this is what you should return, shape-wise):
{"subject":"Chemistry","class":"11","board":"CBSE","books":["NCERT Chemistry Part 1","NCERT Chemistry Part 2"],"topics":[{"topic":"Some Basic Concepts of Chemistry","topic_prerequisites":["Basic arithmetic"],"subtopics":[{"subtopic":"Mole Concept","prerequisites":[{"id":"pre_basic_mole_0_0","name":"Atomic mass","children":[{"id":"pre_basic_mole_0_1","name":"Molecular mass","children":[]}]}],"marks":5}],"learning_material":["NCERT Chapter 1"],"description":"Fundamental concepts of chemistry"}]}

## 6. When the user asks to add/modify topics on an EXISTING map, merge intelligently:
   keep existing topics unless asked to remove them, and append/edit only what was requested.
   Each topic must still have: topic, topic_prerequisites, subtopics, learning_material, description
   Each subtopic must still have: subtopic, prerequisites, marks

REMEMBER: RETURN ONLY THE JSON OBJECT. NOTHING ELSE.`;

// ─── Shared small-font style ──────────────────────────────────────────────────
const fieldSx = {
  '& .MuiInputBase-input': { fontSize: '0.78rem' },
  '& .MuiInputLabel-root': { fontSize: '0.78rem' },
  '& .MuiInputLabel-shrink': { fontSize: '0.82rem' },
};

// ─── Safe immutable nested setter ────────────────────────────────────────────
function setIn(obj: any, pathStr: string, value: any): any {
  const keys = pathStr.split('.');
  function recurse(current: any, depth: number): any {
    const key = keys[depth];
    if (depth === keys.length - 1) {
      if (Array.isArray(current)) {
        const copy = [...current];
        copy[Number(key)] = value;
        return copy;
      }
      return { ...current, [key]: value };
    }
    const next = recurse(current[key], depth + 1);
    if (Array.isArray(current)) {
      const copy = [...current];
      copy[Number(key)] = next;
      return copy;
    }
    return { ...current, [key]: next };
  }
  return recurse(obj, 0);
}

// ─── Debounce hook ────────────────────────────────────────────────────────────
function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const makeShortId = (prefix = 'pre') => `${prefix}_${Math.random().toString(36).slice(2, 8)}`;

const emptySubtopic = () => ({
  subtopic: '',
  prerequisites: '',
  prerequisiteObjects: [] as any[],
  marks: '',
});

const emptyTopic = () => ({
  topic: '',
  topic_prerequisites: '',
  marks: '',
  subtopics: [emptySubtopic()],
  learning_material: '',
  description: '',
});

const initialFormData = {
  subject: '',
  class: '',
  board: '',
  books: '',
  instruction_set: '',
  question_generator_instruction: '',
  diagram_instruction: '',
  video_instruction: '',
  verification_instruction: '',
  step_by_step_solution_instruction: '',
  step_by_step_reasoning_instruction: '',
  sample_example_instruction: '',
  lead_form_id: '',
  campaign_id: '',
  test_instruction_set: '',
  test_lead_form_id: '',
  test_campaign_id: '',
  topics: [emptyTopic()],
};

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  timestamp: Date;
  isLoading?: boolean;
}

// ─── Typing indicator ────────────────────────────────────────────────────────
const TypingIndicator = () => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: '10px',
        bgcolor: 'primary.main',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <SmartToyIcon sx={{ color: '#fff', fontSize: 16 }} />
    </Box>
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        bgcolor: '#fff',
        border: '1px solid #ebebf0',
        borderRadius: '4px 16px 16px 16px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
        display: 'flex',
        gap: 0.5,
        alignItems: 'center',
      }}
    >
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            bgcolor: 'primary.main',
            opacity: 0.5,
            animation: 'bounce 1.2s infinite',
            animationDelay: `${i * 0.2}s`,
            '@keyframes bounce': {
              '0%, 80%, 100%': { transform: 'scale(1)', opacity: 0.5 },
              '40%': { transform: 'scale(1.4)', opacity: 1 },
            },
          }}
        />
      ))}
    </Box>
  </Box>
);

// ─── Main Component ──────────────────────────────────────────────────────────
export default function CreateSubjectMap() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { subjectData } = state || {};

  const [selectedOrganisation, setSelectedOrganisation] = useState<any>(null);
  const { showSnackBar } = useSnackBar();
  const aiActions = AiActions();
  const [copiedJson, setCopiedJson] = useState(false);
  const loggedInUser = useRecoilValue(userInformationAtom);
  const constantActions = ConstantActions();
  const { state: wsState } = useWebSocketAiContext();

  const isSuperUser = loggedInUser?.is_superuser === true || loggedInUser?.profile?.name === 'Admin';
  const hasClassListPermission =
    isSuperUser || constantActions.userHasPermission([globalpermissions?.performancePermissions?.classListAccess]);
  const loggedInOrgId = loggedInUser?.profile?.organisation?.organisation_id || '';

  const [loading, setLoading] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [collapsedTopics, setCollapsedTopics] = useState<Record<number, boolean>>({});
  const [formData, setFormData] = useState<any>(initialFormData);
  const debouncedFormData = useDebounce(formData, 400);

  // ── AI single-query generation state ───────────────────────────────────────
  const [creationMode, setCreationMode] = useState<'manual' | 'ai'>('ai');
  const [aiMessages, setAiMessages] = useState<ChatMessage[]>([]);
  const [aiInput, setAiInput] = useState('');
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [aiConversationId, setAiConversationId] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const lastQueryRef = useRef<string>('');
  const retryCountRef = useRef<number>(0);

  // ── Chat history drawer ─────────────────────────────────────────────────────
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false);
  const [conversationList, setConversationList] = useState<any[]>([]);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [jsonImportValue, setJsonImportValue] = useState('');
  const [jsonImportError, setJsonImportError] = useState('');
  const [showJsonImport, setShowJsonImport] = useState(false);



  // ─── JSON extraction ─────────────────────────────────────────────────────────
  const extractJSONFromResponse = useCallback((text: string): any => {
    if (!text) return null;

    // 1. Whole response is JSON
    try {
      const cleaned = text.replace(/```json\s*|\s*```/g, '').trim();
      const parsed = JSON.parse(cleaned);
      if (parsed.subject && parsed.topics && Array.isArray(parsed.topics)) return parsed;
    } catch {
      /* noop */
    }

    // 2. JSON embedded with subject + topics markers
    try {
      const jsonMatch = text.match(/\{[\s\S]*"subject"[\s\S]*"topics"[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.subject && parsed.topics && Array.isArray(parsed.topics)) return parsed;
      }
    } catch {
      /* noop */
    }

    // 3. Any JSON object at all
    try {
      const jsonMatch = text.match(/\{[\s\S]*\}/);
      if (jsonMatch) {
        const parsed = JSON.parse(jsonMatch[0]);
        if (parsed.subject && parsed.topics && Array.isArray(parsed.topics)) return parsed;
      }
    } catch {
      /* noop */
    }

    return null;
  }, []);



  // ─── Import parsed JSON into the form ────────────────────────────────────────
  const importParsedJson = useCallback(
    (parsed: any) => {
      if (!parsed || !parsed.subject) {
        showSnackBar('Invalid JSON: missing subject field', 'error');
        return;
      }

      const mappedTopics = (parsed.topics || []).map((t: any) => ({
        topic: t.topic || '',
        topic_prerequisites: Array.isArray(t.topic_prerequisites)
          ? t.topic_prerequisites.join(', ')
          : t.topic_prerequisites || '',
        marks: t.marks || '',
        learning_material: Array.isArray(t.learning_material)
          ? t.learning_material.join(', ')
          : t.learning_material || '',
        description: t.description || '',
        subtopics: (t.subtopics || []).map((st: any) => {
          let objs: any[] = [];
          if (Array.isArray(st.prerequisites)) {
            objs = st.prerequisites.map((p: any) => ({
              id: p.id || makeShortId(),
              name: p.name || '',
              children: p.children || [],
            }));
          } else if (typeof st.prerequisites === 'string') {
            objs = st.prerequisites
              .split(',')
              .map((s: string) => s.trim())
              .filter(Boolean)
              .map((s: string) => ({ id: makeShortId(), name: s, children: [] }));
          }
          return {
            subtopic: st.subtopic || '',
            prerequisites: objs.map((o: any) => o.name).join(', '),
            prerequisiteObjects: objs,
            marks: st.marks || '',
          };
        }),
      }));

      if (mappedTopics.length > 5) {
        const allCollapsed: Record<number, boolean> = {};
        mappedTopics.forEach((_: any, i: number) => {
          allCollapsed[i] = true;
        });
        setCollapsedTopics(allCollapsed);
      }

      setFormData((prev: any) => ({
        ...prev,
        subject: parsed.subject || '',
        class: parsed.class || '',
        board: parsed.board || '',
        books: Array.isArray(parsed.books) ? parsed.books.join(', ') : parsed.books || '',
        topics: mappedTopics.length ? mappedTopics : prev.topics,
      }));

      setHasGenerated(true);
      showSnackBar('Knowledge map generated!', 'success');
    },
    [showSnackBar]
  );

  const handleJsonImport = useCallback(() => {
    if (!jsonImportValue.trim()) {
      setJsonImportError('Please paste a JSON object');
      return;
    }

    try {
      // Try to parse the JSON
      let parsedData;

      // If the input is wrapped in ```json or ```, clean it
      let cleanedJson = jsonImportValue.trim();
      cleanedJson = cleanedJson.replace(/```json\s*|\s*```/g, '').trim();
      cleanedJson = cleanedJson.replace(/```\s*|\s*```/g, '').trim();

      parsedData = JSON.parse(cleanedJson);

      // Validate that it has the required structure
      if (!parsedData.subject || !parsedData.topics || !Array.isArray(parsedData.topics)) {
        setJsonImportError('Invalid knowledge map format. Expected "subject" and "topics" array.');
        return;
      }

      // Import the JSON
      importParsedJson(parsedData);
      setJsonImportValue('');
      setJsonImportError('');
      setShowJsonImport(false);
      setCreationMode('manual');
      showSnackBar('Knowledge map imported successfully!', 'success');

    } catch (error) {
      console.error('JSON import error:', error);
      setJsonImportError('Invalid JSON format. Please check your JSON syntax.');
    }
  }, [jsonImportValue, importParsedJson, showSnackBar]);

  const getSampleJSON = useCallback(() => {
    return `{
  "subject": "Computer Science",
  "class": "12",
  "board": "CBSE",
  "books": ["NCERT Computer Science Class 12"],
  "topics": [
    {
      "topic": "Computational Thinking and Programming",
      "topic_prerequisites": ["Basic Python", "Data Types"],
      "description": "Programming concepts and computational thinking",
      "learning_material": ["NCERT Chapter 1", "Practice Exercises"],
      "subtopics": [
        {
          "subtopic": "Python Functions",
          "marks": 8,
          "prerequisites": [
            {
              "id": "pre_python_functions_0_0",
              "name": "Python Basics",
              "children": [
                {
                  "id": "pre_python_functions_0_1",
                  "name": "Variables and Data Types",
                  "children": []
                }
              ]
            }
          ]
        }
      ]
    }
  ]
}`;
  }, []);

  // ─── Load data when editing an existing map ─────────────────────────────────
  useEffect(() => {
    if (!subjectData) return;

    setIsEditMode(true);
    setEditId(subjectData._id);
    setCreationMode('manual');
    setHasGenerated(true);

    const mappedTopics = (subjectData.topics || []).map((t: any) => ({
      topic: t.topic || '',
      topic_prerequisites: Array.isArray(t.topic_prerequisites)
        ? t.topic_prerequisites.join(', ')
        : t.topic_prerequisites || '',
      marks: t.marks || '',
      learning_material: Array.isArray(t.learning_material)
        ? t.learning_material.join(', ')
        : t.learning_material || '',
      description: t.description || '',
      subtopics: (t.subtopics || []).map((st: any) => ({
        subtopic: st.subtopic || '',
        prerequisites: (st.prerequisites || []).map((o: any) => o.name).join(', '),
        prerequisiteObjects: st.prerequisites || [],
        marks: st.marks || '',
      })),
    }));

    setFormData({
      ...initialFormData,
      subject: subjectData.subject || '',
      class: subjectData.class || '',
      board: subjectData.board || '',
      instruction_set: subjectData.instruction_set || '',
      question_generator_instruction: subjectData.question_generator_instruction || '',
      verification_instruction: subjectData.verification_instruction || '',
      step_by_step_solution_instruction: subjectData.step_by_step_solution_instruction || '',
      step_by_step_reasoning_instruction: subjectData.step_by_step_reasoning_instruction || '',
      sample_example_instruction: subjectData.sample_example_instruction || '',
      lead_form_id: subjectData.lead_form_id || '',
      campaign_id: subjectData.campaign_id || '',
      test_instruction_set: subjectData.test_instruction_set || '',
      test_lead_form_id: subjectData.test_lead_form_id || '',
      test_campaign_id: subjectData.test_campaign_id || '',
      books: Array.isArray(subjectData.books) ? subjectData.books.join(', ') : subjectData.books || '',
      topics: mappedTopics.length ? mappedTopics : initialFormData.topics,
      diagram_instruction: subjectData.diagram_instruction || '',
      video_instruction: subjectData.video_instruction || '',
    });

    if (isSuperUser && subjectData.organisation_id) {
      setSelectedOrganisation({ organisation_id: subjectData.organisation_id });
    }

    if (mappedTopics.length > 5) {
      const allCollapsed: Record<number, boolean> = {};
      mappedTopics.forEach((_: any, i: number) => {
        allCollapsed[i] = true;
      });
      setCollapsedTopics(allCollapsed);
    }
  }, [subjectData]);

  // ─── Scroll chat to bottom ────────────────────────────────────────────────────
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [aiMessages]);

  // ─── Handle async AI websocket responses (if backend streams over WS) ───────
  useEffect(() => {
    if (!wsState?.aiRes) return;

    const response = wsState.aiRes;
    setAiMessages((prev) => prev.filter((m) => !m.isLoading));
    setIsAiLoading(false);

    if (response?.success === false) {
      const raw = response?.error || 'Something went wrong.';
      const isQuotaError = raw.includes('insufficient_quota') || raw.includes('429');
      const msg = isQuotaError ? 'AI quota exceeded. Please check your plan or billing.' : raw.length > 100 ? raw.slice(0, 100) + '…' : raw;
      showSnackBar(msg, 'error');
      setAiMessages((prev) => [
        ...prev,
        { id: (Date.now() + 1).toString(), role: 'assistant', text: `❌ Error: ${msg}`, timestamp: new Date() },
      ]);
      return;
    }

    let responseText = '';
    if (response?.ai_response) {
      responseText =
        typeof response.ai_response === 'string'
          ? response.ai_response
          : response.ai_response.response || response.ai_response.text || JSON.stringify(response.ai_response);
    } else if (response?.response) {
      responseText =
        typeof response.response === 'string'
          ? response.response
          : response.response.response || response.response.text || JSON.stringify(response.response);
    } else if (response?.data?.response) {
      responseText = response.data.response;
    } else if (response?.text) {
      responseText = response.text;
    } else if (typeof response === 'string') {
      responseText = response;
    } else {
      responseText = JSON.stringify(response, null, 2);
    }

    processAiResponseText(responseText, lastQueryRef.current);
  }, [wsState?.aiRes]);

  // ─── Low-level call to the backend chat endpoint. Returns raw response text.
  // Does NOT touch chat message state — callers decide what/whether to display.
  const sendToAi = useCallback(
    async (query: string) => {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const organizationId = userInfo?.profile?.organisation?.organisation_id || null;
      const userId = userInfo?.id || null;
      const parameters = {
        dashboard: 'knowledge-map',
        user_id: userId,
      };
      const payload = new FormData();
      payload.append('organization_id', organizationId || '');
      // IMPORTANT: the backend's organizationChat only ever forwards `query` to
      // the model — it does not merge a separate `instruction` field into the
      // prompt. So the strict-JSON contract has to live INSIDE `query` itself,
      // or the model will just answer conversationally (as markdown prose).
      payload.append('query', query);
      payload.append('action', 'chat');
      // payload.append('dashboard', 'knowledge-map');
      payload.append('parameters', JSON.stringify(parameters));
      // Kept for backends that DO read this field — harmless if ignored.
      payload.append('instruction', KNOWLEDGE_MAP_INSTRUCTION_SET);

      if (aiConversationId) payload.append('conversation_id', aiConversationId);
      if (isEditMode && editId) payload.append('edit_id', editId);

      if (hasGenerated && (formData.subject || formData.class)) {
        const contextData = {
          subject: formData.subject,
          class: formData.class,
          board: formData.board,
          books: formData.books,
          topics: formData.topics?.map((t: any) => ({
            topic: t.topic,
            topic_prerequisites: t.topic_prerequisites,
            marks: t.marks,
            description: t.description,
            learning_material: t.learning_material,
            subtopics: t.subtopics?.map((st: any) => ({
              subtopic: st.subtopic,
              marks: st.marks,
              prerequisites: st.prerequisiteObjects || [],
            })),
          })),
        };
        payload.append('context', JSON.stringify(contextData));
      }

      const res = await aiActions.organizationChat(payload);

      const conversationId = res?.conversation_id || res?.data?.conversation_id;
      if (conversationId && !aiConversationId) setAiConversationId(conversationId);

      const responseText: string =
        res?.data?.response ?? res?.response ?? (typeof res === 'string' ? res : JSON.stringify(res));

      return responseText;
    },
    [aiActions, aiConversationId, editId, formData, hasGenerated, isEditMode]
  );

  // ─── Shared handler: parse whatever text the AI returned. If it isn't valid
  // JSON, automatically ask the model ONCE to convert its own answer into the
  // required schema before giving up and showing the user an error. ─────────
  const processAiResponseText = useCallback(
    async (responseText: string, originalQuery: string) => {
      const extractedJson = extractJSONFromResponse(responseText);

      if (extractedJson) {
        retryCountRef.current = 0;
        importParsedJson(extractedJson);
        setAiMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 2).toString(),
            role: 'assistant',
            text: `✅ Generated a knowledge map for **${extractedJson.subject}** (Class ${extractedJson.class}, ${extractedJson.board}) with ${extractedJson.topics?.length || 0} topics. Review it below — you can edit anything or ask me to change it.`,
            timestamp: new Date(),
          },
        ]);
        setCreationMode('manual');
        return;
      }

      // Auto-retry once: ask the model to convert its own prose reply into JSON.
      if (retryCountRef.current < 1) {
        retryCountRef.current += 1;
        setAiMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 3).toString(),
            role: 'assistant',
            text: '⏳ Converting that into the knowledge map format...',
            timestamp: new Date(),
            isLoading: true,
          },
        ]);
        try {
          const conversionPrompt = `Convert the content below into ONLY the strict JSON knowledge-map schema described above. Do not lose any topics/subtopics present in it. Content to convert:\n\n${responseText}`;
          const retryText = await sendToAi(conversionPrompt);
          setAiMessages((prev) => prev.filter((m) => !m.isLoading));
          await processAiResponseText(retryText, originalQuery);
        } catch (e) {
          setAiMessages((prev) => prev.filter((m) => !m.isLoading));
          setAiMessages((prev) => [
            ...prev,
            {
              id: (Date.now() + 4).toString(),
              role: 'assistant',
              text: '⚠️ I could not generate a valid knowledge map. Try again, e.g. "Create a Class 11 Computer Science knowledge map" or "Create a Class 11 Chemistry knowledge map".',
              timestamp: new Date(),
            },
          ]);
        }
        return;
      }

      retryCountRef.current = 0;
      setAiMessages((prev) => [
        ...prev,
        {
          id: (Date.now() + 2).toString(),
          role: 'assistant',
          text: '⚠️ I could not generate a valid knowledge map from that request. Try being more specific, e.g. "Create a Class 11 Computer Science knowledge map" or "Create a Class 11 Chemistry knowledge map".',
          timestamp: new Date(),
        },
      ]);
    },
    [extractJSONFromResponse, importParsedJson, sendToAi]
  );

  // ─── Fire a generation request (used for first query, regenerate, and edits) */
  const runAiQuery = useCallback(
    async (query: string) => {
      const trimmed = query.trim();
      if (!trimmed || isAiLoading) return;

      lastQueryRef.current = trimmed;
      retryCountRef.current = 0;

      setAiMessages((prev) => [
        ...prev,
        { id: Date.now().toString(), role: 'user', text: trimmed, timestamp: new Date() },
      ]);
      const loadingId = (Date.now() + 1).toString();
      setAiMessages((prev) => [
        ...prev,
        {
          id: loadingId,
          role: 'assistant',
          text: hasGenerated ? '⏳ Updating your knowledge map...' : '⏳ Generating your knowledge map...',
          timestamp: new Date(),
          isLoading: true,
        },
      ]);

      setAiInput('');
      setIsAiLoading(true);

      try {
        const responseText = await sendToAi(trimmed);
        setAiMessages((prev) => prev.filter((m) => !m.isLoading));
        await processAiResponseText(responseText, trimmed);
      } catch (error) {
        console.error('AI chat error:', error);
        setAiMessages((prev) => prev.filter((m) => !m.isLoading));
        setAiMessages((prev) => [
          ...prev,
          {
            id: (Date.now() + 2).toString(),
            role: 'assistant',
            text: '❌ Failed to generate knowledge map. Please try again.',
            timestamp: new Date(),
          },
        ]);
        showSnackBar('Failed to send message. Please try again.', 'error');
      } finally {
        setIsAiLoading(false);
      }
    },
    [hasGenerated, isAiLoading, processAiResponseText, sendToAi, showSnackBar]
  );

  const handleAiSubmit = useCallback(() => {
    runAiQuery(aiInput);
  }, [aiInput, runAiQuery]);

  const handleRegenerate = useCallback(() => {
    if (!lastQueryRef.current) return;
    runAiQuery(`Regenerate a fresh, complete knowledge map for: ${lastQueryRef.current}`);
  }, [runAiQuery]);

  // ─── Manual form editing helpers ─────────────────────────────────────────────
  const updateForm = useCallback((path: string, value: any) => {
    setFormData((prev: any) => {
      const updated = setIn(prev, path, value);
      const keys = path.split('.');
      if (keys[keys.length - 1] === 'prerequisites') {
        const siblingPath = [...keys.slice(0, -1), 'prerequisiteObjects'].join('.');
        return setIn(updated, siblingPath, []);
      }
      return updated;
    });
  }, []);

  const addTopic = useCallback(() => {
    setFormData((prev: any) => ({ ...prev, topics: [...prev.topics, emptyTopic()] }));
    setHasGenerated(true);
  }, []);

  const removeTopic = useCallback(
    (i: number) => {
      setFormData((prev: any) => {
        if (prev.topics.length <= 1) {
          showSnackBar('At least one topic required', 'warning');
          return prev;
        }
        return { ...prev, topics: prev.topics.filter((_: any, idx: number) => idx !== i) };
      });
    },
    [showSnackBar]
  );

  const addSubtopic = useCallback((tIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      newTopics[tIndex] = { ...newTopics[tIndex], subtopics: [...newTopics[tIndex].subtopics, emptySubtopic()] };
      return { ...prev, topics: newTopics };
    });
  }, []);

  const removeSubtopic = useCallback(
    (tIndex: number, stIndex: number) => {
      setFormData((prev: any) => {
        if (prev.topics[tIndex].subtopics.length <= 1) {
          showSnackBar('At least one subtopic required', 'warning');
          return prev;
        }
        const newTopics = [...prev.topics];
        newTopics[tIndex] = {
          ...newTopics[tIndex],
          subtopics: newTopics[tIndex].subtopics.filter((_: any, i: number) => i !== stIndex),
        };
        return { ...prev, topics: newTopics };
      });
    },
    [showSnackBar]
  );

  const addPrereqChild = useCallback((_tIndex: number, _stIndex: number, parentObj: any) => {
    parentObj.children.push({ id: makeShortId(), name: '', children: [] });
    setFormData((prev: any) => ({ ...prev }));
  }, []);

  const addRootPrereq = useCallback((tIndex: number, stIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: newTopics[tIndex].subtopics.map((s: any, i: number) =>
          i === stIndex
            ? { ...s, prerequisiteObjects: [...s.prerequisiteObjects, { id: makeShortId(), name: '', children: [] }] }
            : s
        ),
      };
      return { ...prev, topics: newTopics };
    });
  }, []);

  const updatePrereqName = useCallback((obj: any, value: string) => {
    obj.name = value;
    setFormData((prev: any) => ({ ...prev }));
  }, []);

  const removePrereqObj = useCallback((arr: any[], index: number) => {
    arr.splice(index, 1);
    setFormData((prev: any) => ({ ...prev }));
  }, []);

  const handleAutoGenerate = useCallback((tIndex: number, stIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      const st = { ...newTopics[tIndex].subtopics[stIndex] };
      if (!st.prerequisiteObjects.length) {
        st.prerequisiteObjects = [{ id: makeShortId(), name: '', children: [] }];
      }
      st.prerequisites = st.prerequisiteObjects.map((p: any) => p.name).join(', ');
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: newTopics[tIndex].subtopics.map((s: any, i: number) => (i === stIndex ? st : s)),
      };
      return { ...prev, topics: newTopics };
    });
  }, []);

  const handleClearAuto = useCallback((tIndex: number, stIndex: number) => {
    setFormData((prev: any) => {
      const newTopics = [...prev.topics];
      newTopics[tIndex] = {
        ...newTopics[tIndex],
        subtopics: newTopics[tIndex].subtopics.map((s: any, i: number) =>
          i === stIndex ? { ...s, prerequisiteObjects: [], prerequisites: '' } : s
        ),
      };
      return { ...prev, topics: newTopics };
    });
  }, []);

  // ─── Chat history ─────────────────────────────────────────────────────────────
  const fetchConversationHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const organizationId = userInfo?.profile?.organisation?.organisation_id || null;

      const payload: any = { organisation_id: organizationId, parameters: { dashboard: 'knowledge-map' } };
      // if (formData.subject) payload.subject = formData.subject;
      // if (formData.class) payload.parameters.class = formData.class;
      // if (formData.board) payload.parameters.board = formData.board;
      // if (formData.topics?.length) {
      //   const topics = formData.topics.filter((t: any) => t.topic).map((t: any) => t.topic);
      //   if (topics.length) payload.parameters.topics = topics.join(',');
      // }

      const response = await aiActions.getConversationList(payload);
      const list =
        response?.data?.conversations ||
        response?.conversations ||
        (Array.isArray(response?.data) ? response.data : null) ||
        (Array.isArray(response) ? response : []);
      setConversationList(list);
    } catch (error) {
      console.error('Error fetching conversation history:', error);
      showSnackBar('Failed to load conversation history', 'error');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const fetchConversationDetails = async (conversationId: string) => {
    try {
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const organizationId = userInfo?.profile?.organisation?.organisation_id || null;
      const payload: any = { organisation_id: organizationId, parameters: { dashboard: 'knowledge-map' } };
      if (formData.subject) payload.subject = formData.subject;
      if (formData.class) payload.parameters.class = formData.class;

      const response = await aiActions.getConversationList(payload);
      const conversations =
        response?.data?.conversations ||
        response?.conversations ||
        (Array.isArray(response?.data) ? response.data : null) ||
        (Array.isArray(response) ? response : []);
      const conv = conversations.find((c: any) => c.conversation_id === conversationId || c.id === conversationId);

      if (!conv) {
        showSnackBar('Conversation not found', 'error');
        return;
      }

      setSelectedConversationId(conversationId);

      if (conv.knowledge_map) {
        importParsedJson(conv.knowledge_map);
        setCreationMode('manual');
        setShowHistoryDrawer(false);
        showSnackBar('Knowledge map loaded from conversation!', 'success');
      } else if (conv.messages?.length) {
        setAiMessages(
          conv.messages.map((msg: any) => ({
            id: msg.id || Date.now().toString(),
            role: msg.role || 'assistant',
            text: msg.text || msg.content || '',
            timestamp: new Date(msg.timestamp || Date.now()),
          }))
        );
        setAiConversationId(conversationId);
        setCreationMode('ai');
        setShowHistoryDrawer(false);
        showSnackBar('Conversation loaded!', 'success');
      } else {
        showSnackBar('No knowledge map found in this conversation', 'info');
      }
    } catch (error) {
      console.error('Error fetching conversation details:', error);
      showSnackBar('Failed to load conversation', 'error');
    }
  };

  // ─── Format for API ─────────────────────────────────────────────────────────
  const formatData = useCallback(
    (fd = formData) => ({
      subject: fd.subject,
      class: fd.class,
      board: fd.board,
      books: (fd.books || '').split(',').map((b: string) => b.trim()).filter(Boolean),
      instruction_set: fd.instruction_set || '',
      question_generator_instruction: fd.question_generator_instruction || '',
      verification_instruction: fd.verification_instruction || '',
      step_by_step_solution_instruction: fd.step_by_step_solution_instruction || '',
      step_by_step_reasoning_instruction: fd.step_by_step_reasoning_instruction || '',
      sample_example_instruction: fd.sample_example_instruction || '',
      lead_form_id: fd.lead_form_id,
      campaign_id: fd.campaign_id,
      test_instruction_set: fd.test_instruction_set || '',
      diagram_instruction: fd.diagram_instruction || '',
      video_instruction: fd.video_instruction || '',
      test_lead_form_id: fd.test_lead_form_id,
      test_campaign_id: fd.test_campaign_id,
      ...(isSuperUser && selectedOrganisation?.organisation_id
        ? { organisation_id: selectedOrganisation.organisation_id }
        : !isSuperUser && hasClassListPermission && loggedInOrgId
          ? { organisation_id: loggedInOrgId }
          : {}),
      topics: (fd.topics || [])
        .filter((t: any) => t.topic.trim() !== '')
        .map((t: any) => ({
          topic: t.topic,
          topic_prerequisites: (t.topic_prerequisites || '').split(',').map((x: string) => x.trim()).filter(Boolean),
          marks: t.marks ? Number(t.marks) : undefined,
          subtopics: (t.subtopics || [])
            .filter((st: any) => st.subtopic.trim() !== '')
            .map((st: any) => ({
              subtopic: st.subtopic,
              prerequisites: st.prerequisiteObjects || [],
              marks: st.marks ? Number(st.marks) : undefined,
            })),
          learning_material: (t.learning_material || '').split(',').map((x: string) => x.trim()).filter(Boolean),
          description: t.description,
        })),
    }),
    [formData, isSuperUser, hasClassListPermission, selectedOrganisation, loggedInOrgId]
  );

  // ─── Submit / Save ────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const data = formatData();
    if (isSuperUser && !selectedOrganisation) return showSnackBar('Please select an organisation', 'error');
    if (!isSuperUser && hasClassListPermission && !loggedInOrgId)
      return showSnackBar('Your account has no organisation linked. Please contact admin.', 'error');
    if (!data.subject || !data.class || !data.board) return showSnackBar('Fill Subject / Class / Board', 'error');
    if (data.topics.length === 0) return showSnackBar('Add at least one topic', 'error');

    setLoading(true);
    try {
      if (isEditMode && editId) {
        await aiActions.updateKnowledge(editId, data);
        showSnackBar('Updated Successfully!', 'success');
      } else {
        await aiActions.createKnowledge(data);
        showSnackBar('Created Successfully!', 'success');
        setFormData(initialFormData);
        setAiMessages([]);
        setAiConversationId(null);
        setHasGenerated(false);
      }
      setTimeout(() => {
        navigate('/r/student/knowledge-map', {
          state: { selectedSubject: formData.subject, selectedClass: formData.class },
        });
      }, 1500);
    } catch (err) {
      console.error(err);
      showSnackBar('Error saving', 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleTopic = useCallback((idx: number) => setCollapsedTopics((p) => ({ ...p, [idx]: !p[idx] })), []);

  // ─── Recursive prerequisite tree renderer ────────────────────────────────────
  const renderPrereq = (arr: any[], tIndex: number, stIndex: number, isRoot = false): JSX.Element => (
    <>
      {arr.map((p, idx) => (
        <Box key={p.id} sx={{ pl: 1.5, borderLeft: '2px dashed #e0e0e0', mb: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mb: 0.5 }}>
            <TextField
              size="small"
              value={p.name}
              onChange={(e) => updatePrereqName(p, e.target.value)}
              placeholder="Prerequisite Name"
              sx={{ ...fieldSx, width: 200 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={() => addPrereqChild(tIndex, stIndex, p)}
              sx={{ fontSize: '0.68rem', py: 0.3, px: 0.8, minWidth: 'unset', textTransform: 'none' }}
            >
              + Child
            </Button>
            <IconButton size="small" onClick={() => removePrereqObj(arr, idx)}>
              <DeleteIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
          {p.children?.length > 0 && renderPrereq(p.children, tIndex, stIndex, false)}
        </Box>
      ))}
      {isRoot && (
        <Button
          size="small"
          variant="outlined"
          onClick={() => addRootPrereq(tIndex, stIndex)}
          sx={{ fontSize: '0.7rem', mt: 0.5, textTransform: 'none' }}
        >
          + Prerequisite
        </Button>
      )}
    </>
  );

  const sectionTitle = (text: string) => (
    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#546e7a', mb: 1 }}>
      {text}
    </Typography>
  );

  // ─── Preview JSON (debounced) ──────────────────────────────────────────────────
  const previewJson = useMemo(() => {
    try {
      const full = formatData(debouncedFormData);
      if (!isSuperUser) {
        const {
          instruction_set,
          test_instruction_set,
          question_generator_instruction,
          verification_instruction,
          step_by_step_solution_instruction,
          step_by_step_reasoning_instruction,
          sample_example_instruction,
          diagram_instruction,
          video_instruction,
          ...rest
        } = full;
        return JSON.stringify(rest, null, 2);
      }
      return JSON.stringify(full, null, 2);
    } catch {
      return '{}';
    }
  }, [debouncedFormData, formatData, isSuperUser]);

  const quickPrompts = [
    'Create a Class 11 Computer Science knowledge map',
    'Create a Class 11 Chemistry knowledge map',
    'Create a Class 10 Mathematics knowledge map covering the full CBSE syllabus',
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 2, maxWidth: 1200, mx: 'auto', bgcolor: '#f0f2f5', minHeight: '100vh' }}>
      {/* ── TOP HEADER BAR ───────────────────────────────────────────────────── */}
      <Paper elevation={2} sx={{ px: 2.5, py: 1.2, mb: 2, borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton size="small" onClick={() => navigate(-1)} sx={{ color: '#546e7a' }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography sx={{ fontWeight: 700, fontSize: '0.92rem', color: '#1565c0' }}>
            {isEditMode ? 'Edit Subject Knowledge Map' : 'Create Subject Knowledge Map'}
          </Typography>
          {isEditMode && <Chip label="Edit Mode" size="small" color="warning" sx={{ fontSize: '0.65rem', height: 18 }} />}
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setFormData(initialFormData);
              setCollapsedTopics({});
              setAiMessages([]);
              setAiConversationId(null);
              setHasGenerated(false);
              setCreationMode('ai');
            }}
            sx={{ fontSize: '0.72rem', py: 0.5, textTransform: 'none' }}
          >
            Reset
          </Button>
          <Button size="small" variant="outlined" onClick={() => navigate(-1)} sx={{ fontSize: '0.72rem', py: 0.5, textTransform: 'none', color: '#546e7a', borderColor: '#90a4ae' }}>
            Cancel
          </Button>
          <LoadingButton
            buttonName={isEditMode ? 'Update' : 'Create'}
            onClickButton={handleSubmit}
            isLoading={loading}
            variant="contained"
            size="small"
            startIcon={<SaveIcon sx={{ fontSize: 14 }} />}
            sx={{ fontSize: '0.72rem', py: 0.5, textTransform: 'none' }}
          />
        </Box>
      </Paper>

      {/* ── MODE SELECTOR ─────────────────────────────────────────────────────── */}
      <Paper elevation={1} sx={{ px: 2.5, py: 1.5, mb: 2, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#37474f' }}>
              Create Knowledge Map:
            </Typography>
            <ToggleButtonGroup
              value={creationMode}
              exclusive
              onChange={(_, value) => value && setCreationMode(value)}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  textTransform: 'none',
                  fontSize: '0.72rem',
                  px: 1.5,
                  py: 0.4,
                  borderRadius: '8px !important',
                  border: '1px solid #e0e0e0',
                  gap: 0.5,
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: '#fff',
                    borderColor: 'primary.main',
                    '&:hover': { bgcolor: 'primary.dark' },
                  },
                },
              }}
            >
              <ToggleButton value="ai">
                <AutoAwesomeIcon sx={{ fontSize: 14, mr: 0.5 }} />
                With AI
              </ToggleButton>
              <ToggleButton value="manual">
                <EditIcon sx={{ fontSize: 14, mr: 0.5 }} />
                Manually
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {creationMode === 'manual' && (
            <Chip label="Manual Edit Mode" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem', height: 20 }} />
          )}
          {creationMode === 'ai' && (
            <Chip
              label="AI Assisted"
              size="small"
              sx={{ bgcolor: '#f3e5f5', color: '#7b1fa2', fontSize: '0.65rem', height: 20 }}
              icon={<AutoAwesomeIcon sx={{ fontSize: 12 }} />}
            />
          )}
        </Box>
      </Paper>

      {/* ── AI MODE ── Single query -> full auto-generated map ───────────────── */}
      {creationMode === 'ai' && (
        <Paper elevation={1} sx={{ mb: 2, borderRadius: 2, overflow: 'hidden', border: '1px solid #e8ecf4' }}>
          <Box sx={{ px: 2.5, py: 1.2, bgcolor: '#f8f5ff', borderBottom: '1px solid #e8ecf4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SmartToyIcon sx={{ color: '#7b1fa2', fontSize: 18 }} />
              <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#4a148c' }}>
                AI Knowledge Map Generator
              </Typography>
              {hasGenerated && (
                <Chip label={`${formData.subject} · Class ${formData.class}`} size="small" sx={{ fontSize: '0.6rem', height: 18, bgcolor: '#e8eaf6' }} />
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<HistoryIcon sx={{ fontSize: 14 }} />}
                onClick={() => {
                  setShowHistoryDrawer(true);
                  fetchConversationHistory();
                }}
                sx={{ fontSize: '0.7rem', textTransform: 'none', borderColor: '#7b1fa2', color: '#7b1fa2', '&:hover': { bgcolor: '#f3e5f5' } }}
              >
                History
              </Button>
              {hasGenerated && (
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<RefreshIcon sx={{ fontSize: 14 }} />}
                  onClick={handleRegenerate}
                  disabled={isAiLoading}
                  sx={{ fontSize: '0.7rem', textTransform: 'none' }}
                >
                  Regenerate
                </Button>
              )}
              {hasGenerated && (
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<EditIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setCreationMode('manual')}
                  sx={{ fontSize: '0.7rem', textTransform: 'none', bgcolor: '#7b1fa2', '&:hover': { bgcolor: '#6a1b9a' } }}
                >
                  Review & Edit Map
                </Button>
              )}
            </Box>
          </Box>

          <Box sx={{ px: 2.5, py: 1.5, maxHeight: 420, overflowY: 'auto', bgcolor: '#fafbff' }}>
            {aiMessages.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <Typography sx={{ fontSize: '0.85rem', color: '#78909c', mb: 1 }}>
                  Type one line describing the subject and class — I'll generate the full knowledge map
                  (topics, subtopics, prerequisites, marks) in one shot. For example:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                  {quickPrompts.map((p) => (
                    <Chip key={p} label={p} size="small" onClick={() => setAiInput(p)} sx={{ fontSize: '0.65rem', cursor: 'pointer', maxWidth: 320 }} />
                  ))}
                </Box>
              </Box>
            ) : (
              <Box>
                {aiMessages.map((msg) => (
                  <Box
                    key={msg.id}
                    sx={{ display: 'flex', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row', alignItems: 'flex-start', gap: 1.5, mb: 1.5 }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        bgcolor: msg.role === 'user' ? '#e8eaf6' : 'primary.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mt: 0.3,
                      }}
                    >
                      {msg.role === 'user' ? (
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#3949ab' }}>U</Typography>
                      ) : (
                        <SmartToyIcon sx={{ color: '#fff', fontSize: 14 }} />
                      )}
                    </Box>
                    <Box
                      sx={{
                        maxWidth: '80%',
                        px: 2,
                        py: 1.2,
                        borderRadius: msg.role === 'user' ? '16px 4px 16px 16px' : '4px 16px 16px 16px',
                        bgcolor: msg.role === 'user' ? '#e8eaf6' : '#fff',
                        border: msg.role === 'user' ? 'none' : '1px solid #ebebf0',
                        boxShadow: msg.role === 'user' ? 'none' : '0 2px 8px rgba(0,0,0,0.06)',
                      }}
                    >
                      {msg.isLoading ? <CircularProgress size={16} sx={{ color: 'primary.main' }} /> :
                      //  <BeautifyText text={msg.text} />
                       <BeautifyMarkdown text={msg.text} />
                       }
                    </Box>
                  </Box>
                ))}
                {isAiLoading && <TypingIndicator />}
                <div ref={messagesEndRef} />
              </Box>
            )}
          </Box>

          {/* ── Query input ── */}
          <Box sx={{ px: 2.5, py: 1.5, borderTop: '1px solid #e8ecf4', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                fullWidth
                multiline
                maxRows={3}
                size="small"
                placeholder={hasGenerated ? 'Ask me to add/change topics, e.g. "add a topic on Networks"...' : 'e.g. "Create 11th Computer Science" or "Create class 11 Chemistry"'}
                value={aiInput}
                onChange={(e) => setAiInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleAiSubmit();
                  }
                }}
                sx={{ ...fieldSx, '& .MuiInputBase-input': { fontSize: '0.8rem' } }}
              />
              <Button
                variant="contained"
                size="small"
                onClick={handleAiSubmit}
                disabled={!aiInput.trim() || isAiLoading}
                sx={{ minWidth: 100, borderRadius: 2, textTransform: 'none' }}
                endIcon={<SendIcon sx={{ fontSize: 14 }} />}
              >
                {isAiLoading ? 'Generating...' : hasGenerated ? 'Update' : 'Generate'}
              </Button>
            </Box>
            <Typography sx={{ fontSize: '0.6rem', color: '#bdbdbd', mt: 0.5 }}>
              One query generates the entire map — subject, class, board, all topics, subtopics and
              prerequisite trees. Send another message any time to refine it.
            </Typography>
          </Box>
        </Paper>
      )}

      {/* ── MANUAL MODE ── Full form / review editor (auto-filled by AI) ─────── */}
      {creationMode === 'manual' && (
        <>
          {isSuperUser && (
            <Paper elevation={1} sx={{ px: 2.5, py: 1.5, mb: 2, borderRadius: 2 }}>
              {sectionTitle('Organisation & Campaign')}
              <Grid container spacing={1.5} alignItems="flex-end">
                <Grid item xs={12} md={4}>
                  <Typography sx={{ fontSize: '0.72rem', mb: 0.3, color: '#546e7a', fontWeight: 600 }}>Organisation</Typography>
                  <OrganisationDropdown value={selectedOrganisation} onChange={(val) => setSelectedOrganisation(val)} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Campaign ID" fullWidth size="small" value={formData.campaign_id} onChange={(e) => updateForm('campaign_id', e.target.value)} placeholder="Enter campaign ID" sx={fieldSx} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Lead Form ID" fullWidth size="small" value={formData.lead_form_id} onChange={(e) => updateForm('lead_form_id', e.target.value)} placeholder="Enter lead form ID" sx={fieldSx} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Test Campaign ID" fullWidth size="small" value={formData.test_campaign_id} onChange={(e) => updateForm('test_campaign_id', e.target.value)} placeholder="Enter test campaign ID" sx={fieldSx} />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField label="Test Lead Form ID" fullWidth size="small" value={formData.test_lead_form_id} onChange={(e) => updateForm('test_lead_form_id', e.target.value)} placeholder="Enter test lead form ID" sx={fieldSx} />
                </Grid>
              </Grid>
            </Paper>
          )}

          <Paper elevation={1} sx={{ px: 2.5, py: 1.5, mb: 2, borderRadius: 2 }}>
            {sectionTitle('Basic Information')}
            <Grid container spacing={1.5}>
              <Grid item xs={6} md={3}>
                <TextField label="Subject *" fullWidth size="small" value={formData.subject} onChange={(e) => updateForm('subject', e.target.value)} placeholder="Chemistry" sx={fieldSx} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField label="Class *" fullWidth size="small" value={formData.class} onChange={(e) => updateForm('class', e.target.value)} placeholder="11" sx={fieldSx} />
              </Grid>
              <Grid item xs={6} md={2}>
                <TextField label="Board *" fullWidth size="small" value={formData.board} onChange={(e) => updateForm('board', e.target.value)} placeholder="CBSE" sx={fieldSx} />
              </Grid>
              <Grid item xs={6} md={5}>
                <TextField label="Books (comma separated)" fullWidth size="small" value={formData.books} onChange={(e) => updateForm('books', e.target.value)} placeholder="NCERT Chemistry Part 1, Part 2" sx={fieldSx} />
              </Grid>

              {isSuperUser && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField label="Instruction Set" fullWidth size="small" multiline rows={2} value={formData.instruction_set} onChange={(e) => updateForm('instruction_set', e.target.value)} placeholder="Enter general instructions for this subject" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Test Instruction Set" fullWidth size="small" multiline rows={2} value={formData.test_instruction_set} onChange={(e) => updateForm('test_instruction_set', e.target.value)} placeholder="Enter test instructions for this subject" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Question Generator Instruction" fullWidth size="small" multiline rows={2} value={formData.question_generator_instruction} onChange={(e) => updateForm('question_generator_instruction', e.target.value)} placeholder="Enter question generator instructions" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Verification Instruction" fullWidth size="small" multiline rows={2} value={formData.verification_instruction} onChange={(e) => updateForm('verification_instruction', e.target.value)} placeholder="Enter verification instructions" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Step by Step Solution Instruction" fullWidth size="small" multiline rows={2} value={formData.step_by_step_solution_instruction} onChange={(e) => updateForm('step_by_step_solution_instruction', e.target.value)} placeholder="Enter step by step solution instructions" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Step by Step Reasoning Instruction" fullWidth size="small" multiline rows={2} value={formData.step_by_step_reasoning_instruction} onChange={(e) => updateForm('step_by_step_reasoning_instruction', e.target.value)} placeholder="Enter step by step reasoning instructions" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Diagram Instruction" fullWidth size="small" multiline rows={2} value={formData.diagram_instruction} onChange={(e) => updateForm('diagram_instruction', e.target.value)} placeholder="Enter diagram instructions for this subject" sx={fieldSx} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField label="Video Instruction" fullWidth size="small" multiline rows={2} value={formData.video_instruction} onChange={(e) => updateForm('video_instruction', e.target.value)} placeholder="Enter video instructions for this subject" sx={fieldSx} />
                  </Grid>
                </>
              )}
            </Grid>
          </Paper>

          <Paper elevation={1} sx={{ px: 2.5, py: 1.5, mb: 2, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ContentCopyIcon sx={{ color: '#546e7a', fontSize: 20 }} />
                <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#37474f' }}>
                  Import Knowledge Map
                </Typography>
                <Chip label="JSON" size="small" sx={{ fontSize: '0.6rem', bgcolor: '#e3f2fd', color: '#1565c0' }} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {!showJsonImport ? (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setShowJsonImport(true)}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                  >
                    Paste JSON
                  </Button>
                ) : (
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => {
                      setShowJsonImport(false);
                      setJsonImportValue('');
                      setJsonImportError('');
                    }}
                    sx={{ fontSize: '0.72rem', textTransform: 'none' }}
                  >
                    Cancel
                  </Button>
                )}
                {!showJsonImport && hasGenerated && (
                  <Chip
                    label="Current map loaded"
                    size="small"
                    color="success"
                    sx={{ fontSize: '0.6rem', height: 20 }}
                  />
                )}
              </Box>
            </Box>

            {showJsonImport && (
              <Box sx={{ mt: 2 }}>
                <TextField
                  fullWidth
                  multiline
                  rows={8}
                  size="small"
                  placeholder='Paste your JSON knowledge map here...'
                  value={jsonImportValue}
                  onChange={(e) => {
                    setJsonImportValue(e.target.value);
                    setJsonImportError('');
                  }}
                  error={!!jsonImportError}
                  helperText={jsonImportError}
                  sx={{
                    ...fieldSx,
                    '& .MuiInputBase-root': {
                      fontFamily: 'monospace',
                      fontSize: '0.75rem',
                      backgroundColor: '#fafafa',
                    },
                  }}
                />

                <Box sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={handleJsonImport}
                    disabled={!jsonImportValue.trim()}
                    sx={{ textTransform: 'none' }}
                  >
                    Import Map
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      setJsonImportValue(getSampleJSON());
                      setJsonImportError('');
                    }}
                    sx={{ textTransform: 'none' }}
                  >
                    Load Sample JSON
                  </Button>
                  <Typography variant="caption" color="textSecondary" sx={{ alignSelf: 'center', fontSize: '0.65rem' }}>
                    Paste a valid knowledge map JSON to auto-populate the form
                  </Typography>
                </Box>
              </Box>
            )}
          </Paper>


          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            {sectionTitle(`Topics (${formData.topics.length})`)}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<AutoAwesomeIcon sx={{ fontSize: 14 }} />}
                onClick={() => setCreationMode('ai')}
                sx={{ fontSize: '0.68rem', textTransform: 'none', py: 0.2 }}
              >
                Back to AI Chat
              </Button>
              {formData.topics.length > 3 && (
                <>
                  <Button
                    size="small"
                    sx={{ fontSize: '0.68rem', textTransform: 'none', py: 0.2 }}
                    onClick={() => {
                      const all: Record<number, boolean> = {};
                      formData.topics.forEach((_: any, i: number) => {
                        all[i] = true;
                      });
                      setCollapsedTopics(all);
                    }}
                  >
                    Collapse All
                  </Button>
                  <Button size="small" sx={{ fontSize: '0.68rem', textTransform: 'none', py: 0.2 }} onClick={() => setCollapsedTopics({})}>
                    Expand All
                  </Button>
                </>
              )}
            </Box>
          </Box>

          {formData.topics.map((topic: any, tIndex: number) => {
            const isCollapsed = !!collapsedTopics[tIndex];
            const subtopicCount = topic.subtopics?.length || 0;
            return (
              <Paper key={tIndex} elevation={1} sx={{ mb: 1.5, borderRadius: 2, overflow: 'hidden', border: '1px solid #e3e8ef' }}>
                <Box
                  onClick={() => toggleTopic(tIndex)}
                  sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#e8f0fe', cursor: 'pointer', userSelect: 'none', '&:hover': { bgcolor: '#d6e4fd' } }}
                >
                  <IconButton size="small" sx={{ p: 0.2 }} onClick={(e) => { e.stopPropagation(); toggleTopic(tIndex); }}>
                    {isCollapsed ? <ExpandMoreIcon fontSize="small" /> : <ExpandLessIcon fontSize="small" />}
                  </IconButton>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1a3a6e', flexGrow: 1 }}>
                    Topic {tIndex + 1}{topic.topic ? `: ${topic.topic}` : ''}
                  </Typography>
                  <Chip label={`${subtopicCount} subtopic${subtopicCount !== 1 ? 's' : ''}`} size="small" sx={{ fontSize: '0.65rem', height: 18, bgcolor: '#c5cae9' }} />
                  {topic.marks && <Chip label={`${topic.marks} marks`} size="small" color="primary" sx={{ fontSize: '0.65rem', height: 18 }} />}
                  {formData.topics.length > 1 && (
                    <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); removeTopic(tIndex); }} sx={{ ml: 0.5 }}>
                      <DeleteIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  )}
                </Box>

                <Collapse in={!isCollapsed} unmountOnExit>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    <Grid container spacing={1.5}>
                      <Grid item xs={12} md={5}>
                        <TextField fullWidth size="small" label="Topic Name" value={topic.topic} onChange={(e) => updateForm(`topics.${tIndex}.topic`, e.target.value)} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} md={5}>
                        <TextField fullWidth size="small" label="Topic Prerequisites" value={topic.topic_prerequisites} onChange={(e) => updateForm(`topics.${tIndex}.topic_prerequisites`, e.target.value)} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} md={2}>
                        <TextField fullWidth size="small" type="number" label="Topic Marks" value={topic.marks || ''} onChange={(e) => updateForm(`topics.${tIndex}.marks`, e.target.value)} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="small" label="Learning Material (comma separated)" value={topic.learning_material} onChange={(e) => updateForm(`topics.${tIndex}.learning_material`, e.target.value)} sx={fieldSx} />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <TextField fullWidth size="small" label="Description" value={topic.description} onChange={(e) => updateForm(`topics.${tIndex}.description`, e.target.value)} sx={fieldSx} />
                      </Grid>
                    </Grid>

                    <Divider sx={{ my: 1.5 }} />
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#546e7a', mb: 1, textTransform: 'uppercase', letterSpacing: 0.6 }}>
                      Subtopics
                    </Typography>

                    {topic.subtopics.map((subtopic: any, stIndex: number) => (
                      <Box key={stIndex} sx={{ bgcolor: '#fafbff', border: '1px solid #e8ecf4', borderRadius: 1.5, p: 1.5, mb: 1 }}>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={12} md={5}>
                            <TextField fullWidth size="small" label="Subtopic Name" value={subtopic.subtopic} onChange={(e) => updateForm(`topics.${tIndex}.subtopics.${stIndex}.subtopic`, e.target.value)} sx={fieldSx} />
                          </Grid>
                          <Grid item xs={6} md={2}>
                            <TextField fullWidth size="small" type="number" label="Marks" value={subtopic.marks || ''} onChange={(e) => updateForm(`topics.${tIndex}.subtopics.${stIndex}.marks`, e.target.value)} sx={fieldSx} />
                          </Grid>
                          <Grid item xs={6} md={5} sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap', justifyContent: { sm: 'unset', xs: 'space-between' } }}>
                            <Tooltip title="Add prerequisites">
                              <Button size="small" variant="outlined" startIcon={<AutorenewIcon sx={{ fontSize: 12 }} />} onClick={() => handleAutoGenerate(tIndex, stIndex)} sx={{ fontSize: '0.68rem', py: 0.3, textTransform: 'none', px: 0.8, width: { sm: 'auto', xs: '157px' } }}>
                                Add prerequisites
                              </Button>
                            </Tooltip>
                            <Tooltip title="Clear prerequisites">
                              <IconButton size="small" onClick={() => handleClearAuto(tIndex, stIndex)}>
                                <ClearIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                            <Button size="small" variant="outlined" onClick={() => addSubtopic(tIndex)} sx={{ fontSize: '0.68rem', py: 0.3, textTransform: 'none', px: 0.8, width: { sm: 'auto', xs: '157px' } }}>
                              Add Subtopic
                            </Button>
                            <IconButton size="small" color="error" onClick={() => removeSubtopic(tIndex, stIndex)}>
                              <DeleteIcon sx={{ fontSize: 14 }} />
                            </IconButton>
                          </Grid>
                        </Grid>

                        {subtopic.prerequisiteObjects?.length > 0 && (
                          <Box sx={{ mt: 1, pl: 0.5 }}>
                            <Typography sx={{ fontSize: '0.66rem', color: '#78909c', mb: 0.5, fontWeight: 600 }}>
                              Prerequisite Tree
                            </Typography>
                            {renderPrereq(subtopic.prerequisiteObjects, tIndex, stIndex, true)}
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Collapse>
              </Paper>
            );
          })}

          <Button
            fullWidth
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addTopic}
            sx={{ py: 1, mb: 2, fontSize: '0.78rem', border: '1.5px dashed #90a4ae', color: '#546e7a', borderRadius: 2, textTransform: 'none', '&:hover': { bgcolor: '#e8f0fe', borderColor: '#1976d2', color: '#1976d2' } }}
          >
            Add Topic
          </Button>

          <Paper elevation={1} sx={{ px: 2.5, py: 1.5, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              {sectionTitle('Preview JSON')}
              <Tooltip title={copiedJson ? 'Copied!' : 'Copy JSON'}>
                <IconButton
                  size="small"
                  onClick={() => {
                    navigator.clipboard.writeText(previewJson);
                    setCopiedJson(true);
                    setTimeout(() => setCopiedJson(false), 2000);
                  }}
                  sx={{ color: copiedJson ? '#43a047' : '#546e7a' }}
                >
                  {copiedJson ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            </Box>
            <Box component="pre" sx={{ overflow: 'auto', maxHeight: 400, bgcolor: '#f5f5f5', p: 2, borderRadius: 1, fontSize: '0.68rem', lineHeight: 1.6 }}>
              {previewJson}
            </Box>
          </Paper>
        </>
      )}

      {/* ── CHAT HISTORY DRAWER ─────────────────────────────────────────────── */}
      <Drawer
        anchor="right"
        open={showHistoryDrawer}
        onClose={() => setShowHistoryDrawer(false)}
        sx={{ '& .MuiDrawer-paper': { width: 380, maxWidth: '90vw', bgcolor: '#f8f9fa', p: 2 } }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 1 }}>
            <HistoryIcon sx={{ color: '#7b1fa2' }} />
            Chat History
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip label="Knowledge Map" size="small" sx={{ fontSize: '0.6rem', bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
            <IconButton onClick={() => setShowHistoryDrawer(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 0.5, mb: 1.5, flexWrap: 'wrap' }}>
          <Chip label="Dashboard: Knowledge Map" size="small" sx={{ fontSize: '0.65rem', bgcolor: '#e8eaf6' }} />
          {formData.subject && <Chip label={`Subject: ${formData.subject}`} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#e8eaf6' }} />}
          {formData.class && <Chip label={`Class: ${formData.class}`} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#e8eaf6' }} />}
          {formData.board && <Chip label={`Board: ${formData.board}`} size="small" sx={{ fontSize: '0.65rem', bgcolor: '#e8eaf6' }} />}
        </Box>

        {isLoadingHistory ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={30} />
          </Box>
        ) : conversationList.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <ChatIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography sx={{ color: '#999', fontSize: '0.9rem' }}>No conversations yet</Typography>
            <Typography sx={{ color: '#bbb', fontSize: '0.8rem', mt: 0.5 }}>
              {formData.subject || formData.class || formData.board ? 'Try adjusting your filters or start a new AI chat' : 'Start a new AI chat to generate knowledge maps'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {conversationList.map((conv, index) => (
              <ListItem key={conv.id || index} disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  selected={conv.conversation_id === selectedConversationId}
                  onClick={() => fetchConversationDetails(conv.conversation_id)}
                  sx={{ borderRadius: 1, bgcolor: conv.conversation_id === selectedConversationId ? 'rgba(124,58,237,0.08)' : 'transparent', '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' }, p: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    <ChatIcon sx={{ color: conv.knowledge_map ? '#7b1fa2' : '#999', fontSize: 18 }} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: conv.knowledge_map ? 600 : 400, color: conv.knowledge_map ? '#1a1a2e' : '#555' }}>
                        {conv.query || 'Untitled Conversation'}
                      </Typography>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', mt: 0.3, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '0.65rem', color: '#999' }}>
                          {conv.created_at ? new Date(conv.created_at).toLocaleDateString() : ''}
                        </Typography>
                        {conv.dashboard && <Chip label={conv.dashboard} size="small" sx={{ fontSize: '0.5rem', height: 16, bgcolor: '#fce4ec', color: '#c62828' }} />}
                        {conv.subject && <Chip label={conv.subject} size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#e3f2fd', color: '#1565c0' }} />}
                        {conv.knowledge_map && <Chip label="Has Map" size="small" sx={{ fontSize: '0.55rem', height: 16, bgcolor: '#f3e5f5', color: '#7b1fa2' }} />}
                      </Box>
                    }
                    secondaryTypographyProps={{ component: 'div' }}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Drawer>
    </Box>
  );
}